import React, { useRef } from 'react'
import { components, createFilter } from 'react-select'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'

import { fetchIngredients } from '@/api'
import { Flex } from '@/components/Flex'
import Fuse from 'fuse.js'
import { withAsyncPaginate } from 'react-select-async-paginate'
import { Select } from '@/components/Select'

export type Ing = {
  idIngredient: number
  name: string
  englishName: string
}

type Props = {
  onChange: (value: Ing[]) => void
}

const SelectPaginate = withAsyncPaginate(Select as any)

export default function IngredientsSearch({ onChange }: Props) {
  const dataPromiseRef = useRef<Ing[] | null>(null)
  const fuseRef = useRef<Fuse<Ing> | null>(null)
  const [currentSelection, setCurrentSelection] = React.useState<Ing[]>([])

  async function loadOptions(search, _loadedOptions: Ing[], { page }) {
    if (!dataPromiseRef.current) {
      dataPromiseRef.current = await fetchIngredients({ onlyUsedInRecipes: true })
    }
    let data = dataPromiseRef.current

    if (search) {
      if (!fuseRef.current) {
        fuseRef.current = new Fuse(data, {
          keys: ['name', 'englishName'],
          threshold: 0.3,
        })
      }

      data = fuseRef.current.search(search).map((r) => r.item)
    }

    const offset = (page - 1) * 100

    return {
      options: data?.slice(offset, offset + 100),
      hasMore: data?.length > offset + 100,
      additional: {
        page: page + 1,
      },
    }
  }

  function handleChange(value: Ing[]) {
    setCurrentSelection(value)
    onChange(value)
  }

  return (
    <SelectPaginate
      isMulti
      loadOptions={loadOptions}
      filterOption={createFilter({ ignoreAccents: false })}
      additional={{
        page: 1,
      }}
      value={currentSelection}
      formatOptionLabel={(item) => (
        <Flex direction="column" gap={2}>
          <div>{item?.name}</div>
          <div style={{ fontSize: 'small', color: 'gray' }}>{item?.englishName}</div>
        </Flex>
      )}
      getOptionLabel={(item) => (item ? item.name + ' ' + item.englishName : '')}
      getOptionValue={(item) => (item ? item.idIngredient.toString() : '')}
      onChange={handleChange}
      components={{
        Placeholder,
        IndicatorSeparator: null,
      }}
      styles={{
        control: (baseStyles) => ({
          ...baseStyles,
          marginTop: 8,
          padding: 4,
          borderColor: 'var(--border-color)',
          backgroundColor: 'var(--field-background)',
        }),
        placeholder: (baseStyles) => ({
          ...baseStyles,
        }),
      }}
    />
  )
}

function Placeholder(props) {
  return (
    <components.Placeholder {...props}>
      <FontAwesomeIcon icon={faSearch} />
    </components.Placeholder>
  )
}
